<script setup>
import { nextTick, useTemplateRef, computed, onMounted, ref } from 'vue'
import { useAppStore } from '@/stores/appStore.js'
import { storeToRefs } from 'pinia'
import EpicWidgetComponent from '@/components/EpicWidgetComponent.vue'

const appStore = useAppStore()
const {
  selectedSpecialtyName,
  selectedSpecialtyVisitType,
  selectedDepartmentIds,
  selectedProviders,
  specialtyOptions,
} = storeToRefs(appStore)

const widgetEl = useTemplateRef('widgetEl')
// Bumped on "Start Over" to force EpicWidgetComponent to remount even when the
// specialty hasn't changed — the widget has no internal back button.
const widgetResetCount = ref(0)

const sortedSpecialties = computed(() => {
  return specialtyOptions.value
    .slice()
    .sort((a, b) =>
      a.name === 'Primary Care' ? -1 : b.name === 'Primary Care' ? 1 : a.name.localeCompare(b.name),
    )
})

// Also used to match the ?specialty= deep-link param from CTAs on other pages.
function slugify(name) {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
}

async function selectSpecialty(specialtyName) {
  appStore.setSelectedSpecialty(specialtyName)
  await nextTick()
  widgetEl.value?.$el?.scrollIntoView?.({ behavior: 'smooth', block: 'start' })
}

function startOver() {
  widgetResetCount.value++
}

// Lets a "Schedule" CTA on another page (e.g. a service page) deep-link straight into a
// specialty here, e.g. /find-care/book-an-appointment?specialty=primary-care
onMounted(() => {
  const requested = new URLSearchParams(window.location.search).get('specialty')
  if (!requested) return
  const match = sortedSpecialties.value.find((s) => slugify(s.name) === requested)
  if (match) selectSpecialty(match.name)
})
</script>

<template>
  <div class="u-spaceItems4 u-spaceTop6">
    <div class="Input-group u-spaceItems04">
      <h2 class="Groove u-padBottom04">Which specialty would you like to schedule with?</h2>
      <ul
        class="Grid Grid--withGap u-listReset u-spaceTop3"
        role="group"
        aria-label="Select a specialty to start scheduling an appointment"
      >
        <li
          class="Grid-cell u-sizeFull u-sm-size1of2 u-md-size1of3"
          v-for="specialty in sortedSpecialties"
          :key="specialty.visitType"
        >
          <button
            :id="'specialty-' + slugify(specialty.name)"
            type="button"
            class="Button u-sizeFull"
            :class="
              selectedSpecialtyName === specialty.name ? 'Button--primary' : 'Button--tertiary'
            "
            :aria-pressed="selectedSpecialtyName === specialty.name"
            aria-controls="epicWidgetContainer"
            @click="selectSpecialty(specialty.name)"
          >
            {{ specialty.name }}
            <span v-if="selectedSpecialtyName === specialty.name">
              <svg
                class="Icon u-spaceLeft09"
                xmlns="http://www.w3.org/2000/svg"
                width="64"
                height="64"
                viewBox="0 0 24 24"
                aria-hidden="true"
                role="presentation"
              >
                <path
                  d="M18.5,4.5L9.6,16.5c-0.5,0.7-0.4,1.6,0.3,2.1c0.7,0.5,1.6,0.4,2.1-0.3l8.9-12.1c0.5-0.7,0.4-1.6-0.3-2.1C19.9,3.7,19,3.8,18.5,4.5"
                />
                <path
                  d="M3.4,13.9l6.4,4.8c0.7,0.5,1.6,0.4,2.1-0.3c0.5-0.7,0.4-1.6-0.3-2.1l-6.4-4.8c-0.7-0.5-1.6-0.4-2.1,0.3S2.8,13.4,3.4,13.9"
                />
              </svg>
            </span>
          </button>
        </li>
      </ul>
      <!--<p id="specialtyHelpText" class="u-textBlackAlt u-textShrink1">
        Choose a specialty to schedule an appointment with. If you don't see your desired specialty,
        please call <a href="tel:+19042022000">904.202.2000</a>.
      </p>-->
    </div>
    <div v-if="selectedSpecialtyName !== ''" class="u-textRight">
      <button type="button" class="Button Button--link Button--compact" @click="startOver">
        <span>
          <svg
            class="Icon u-textGrow2 u-spaceRight09"
            xmlns="http://www.w3.org/2000/svg"
            version="1.1"
            viewBox="0 0 64 64"
            width="64"
            height="64"
            aria-hidden="true"
            role="presentation"
          >
            <path
              d="M19.8,18.6c.9-.8.9-2.1.2-2.9s-2-.8-2.9,0c-4.4,4-7,9.7-7.1,15.6-.3,12.5,9.8,22.8,22.3,22.7,11.9-.1,21.6-9.7,21.8-21.6.2-11.6-8.6-21.4-20.2-22.4l2.6-2.6c.8-.8.8-2,0-2.8s-2-.9-2.8,0l-6.3,6.3c-.7.7-.7,1.9,0,2.6l6.3,6.3c.8.8,2.1.6,2.8-.1.7-.8.7-2,0-2.8l-2.9-2.9c4,.3,7.8,2,10.7,4.8,4.3,4,6.3,9.7,5.6,15.5-.6,5.3-3.7,10.2-8.4,13.1-5.8,3.6-13.3,3.6-19.1,0-4.7-2.9-7.8-7.9-8.4-13.2-.7-5.8,1.4-11.5,5.8-15.5h0Z"
            />
            <path
              d="M19.8,18.6c-4.3,4-6.4,9.7-5.8,15.5.6,5.4,3.7,10.3,8.4,13.2,5.8,3.6,13.3,3.6,19.1,0,4.7-2.9,7.7-7.8,8.4-13.1.7-5.8-1.4-11.5-5.6-15.5-2.9-2.7-6.7-4.5-10.7-4.8l2.9,2.9c.8.8.8,2,0,2.8-.7.8-2,.9-2.8.1l-6.3-6.3c-.7-.7-.6-1.9,0-2.6l6.3-6.3c.8-.8,2.1-.6,2.8,0,.7.8.7,2,0,2.8l-2.6,2.6c11.6,1,20.4,10.8,20.2,22.4-.2,11.9-9.9,21.5-21.8,21.6-12.5.1-22.6-10.2-22.3-22.7.2-6,2.8-11.6,7.1-15.6.8-.8,2.1-.8,2.9,0s.7,2.1-.2,2.9h0Z"
            />
          </svg>
        </span>
        <span>Start Over</span>
      </button>
    </div>
    <EpicWidgetComponent
      v-if="selectedSpecialtyName !== ''"
      ref="widgetEl"
      :key="selectedSpecialtyName + '-' + widgetResetCount"
      :id="selectedDepartmentIds"
      :name="selectedSpecialtyName"
      :visitType="selectedSpecialtyVisitType"
      :provider="selectedProviders"
    />
  </div>
</template>
